import React, { useState, useEffect, useRef, useCallback } from 'react';
import useThemeStore from "../../store/themeStore";
import { ThemeProps } from "../../utils/theme";

interface CalendarModalComponentProps {
  isOpen: boolean;
  onClose: () => void;
  calendarLink: string;
  serviceName: string;
  onBookingComplete?: (bookingData: any) => void;
}

const CalendarModal: React.FC<CalendarModalComponentProps> = ({
  isOpen,
  onClose,
  calendarLink,
  serviceName,
  onBookingComplete
}) => {
  const [timeSpent, setTimeSpent] = useState(0);
  const [bookingDetected, setBookingDetected] = useState(false);
  const [detectedBookingData, setDetectedBookingData] = useState<any>(null);
  const [showSuccessState, setShowSuccessState] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const themes = useThemeStore((state) => state.theme as unknown as ThemeProps);

  // Helper functions for comprehensive data extraction
  const findEmailInObject = useCallback((obj: any): string | null => {
    if (!obj || typeof obj !== 'object') return null;
    
    const emailKeys = ['email', 'user_email', 'attendee_email', 'customer_email', 'client_email'];
    
    for (const key of emailKeys) {
      if (obj[key] && typeof obj[key] === 'string' && obj[key].includes('@')) {
        return obj[key];
      }
    }
    
    // Recursive search
    for (const value of Object.values(obj)) {
      if (value && typeof value === 'object') {
        const found = findEmailInObject(value);
        if (found) return found;
      }
    }
    
    return null;
  }, []);

  const findNameInObject = useCallback((obj: any): string | null => {
    if (!obj || typeof obj !== 'object') return null;
    
    const nameKeys = ['name', 'full_name', 'user_name', 'attendee_name', 'customer_name', 'client_name'];
    
    for (const key of nameKeys) {
      if (obj[key] && typeof obj[key] === 'string') {
        return obj[key];
      }
    }
    
    // Try first_name + last_name
    if (obj.first_name && obj.last_name) {
      return `${obj.first_name} ${obj.last_name}`;
    }
    
    // Recursive search
    for (const value of Object.values(obj)) {
      if (value && typeof value === 'object') {
        const found = findNameInObject(value);
        if (found) return found;
      }
    }
    
    return null;
  }, []);

  const findDateTimeInObject = useCallback((obj: any): string | null => {
    if (!obj || typeof obj !== 'object') return null;
    
    const dateKeys = [
      'startTime', 'start_time', 'starts_at', 'dateTime', 'date_time',
      'scheduledTime', 'scheduled_time', 'appointmentTime', 'appointment_time',
      'meetingTime', 'meeting_time', 'bookingTime', 'booking_time'
    ];
    
    for (const key of dateKeys) {
      if (obj[key]) {
        const date = new Date(obj[key]);
        if (!isNaN(date.getTime())) {
          return obj[key];
        }
      }
    }
    
    // Recursive search
    for (const value of Object.values(obj)) {
      if (value && typeof value === 'object') {
        const found = findDateTimeInObject(value);
        if (found) return found;
      }
    }
    
    return null;
  }, []);

  const getProviderFromOrigin = useCallback((origin: string): string => {
    if (origin.includes('tidycal')) return 'tidycal';
    if (origin.includes('calendly')) return 'calendly';
    if (origin.includes('cal.com')) return 'cal.com';
    if (origin.includes('acuity')) return 'acuity';
    if (origin.includes('savvycal')) return 'savvycal';
    return 'unknown';
  }, []);

  // Calendar provider detection functions
  const isTidyCalBooking = useCallback((data: any): boolean => {
    return data?.type === 'tidycal:booking_completed' ||
           data?.event === 'booking_created' ||
           data?.action === 'booking_confirmed' ||
           (data?.type?.includes('booking') && data?.email) ||
           data?.tidycal_booking === true;
  }, []);

  const extractTidyCalData = useCallback((data: any) => ({
    provider: 'tidycal',
    email: data.email || data.attendee?.email || data.booking?.email,
    name: data.name || data.attendee?.name || data.booking?.name,
    startTime: data.startTime || data.starts_at || data.start_time,
    endTime: data.endTime || data.ends_at || data.end_time,
    timezone: data.timezone || data.tz,
    eventType: data.eventType || data.event_type?.name,
    bookingId: data.id || data.booking_id,
    rawData: data,
    confidence: 'high'
  }), []);

  const isCalendlyBooking = useCallback((data: any): boolean => {
    return data?.event === 'calendly.event_scheduled' ||
           data?.type === 'calendly:event_scheduled' ||
           data?.webhook?.event === 'invitee.created' ||
           (data?.payload?.event_type && data?.payload?.invitee);
  }, []);

  const extractCalendlyData = useCallback((data: any) => {
    const payload = data.payload || data;
    const invitee = payload.invitee || payload.attendee;
    const event = payload.event || payload.scheduled_event;
    
    return {
      provider: 'calendly',
      email: invitee?.email,
      name: invitee?.name,
      startTime: event?.start_time,
      endTime: event?.end_time, 
      timezone: event?.timezone,
      eventType: payload.event_type?.name,
      bookingId: event?.uuid || invitee?.uuid,
      rawData: data,
      confidence: 'high'
    };
  }, []);

  const isCalComBooking = useCallback((data: any): boolean => {
    return data?.type === 'cal:booking_successful' ||
           data?.event === 'booking:successful' ||
           data?.action === 'booking.created' ||
           (data?.booking && data?.attendees);
  }, []);

  const extractCalComData = useCallback((data: any) => {
    const booking = data.booking || data;
    const attendee = booking.attendees?.[0] || data.attendee;
    
    return {
      provider: 'cal.com',
      email: attendee?.email,
      name: attendee?.name,
      startTime: booking.startTime,
      endTime: booking.endTime,
      timezone: booking.timeZone,
      eventType: booking.eventType?.title,
      bookingId: booking.id,
      rawData: data,
      confidence: 'high'
    };
  }, []);

  const isGenericSuccessMessage = useCallback((data: any): boolean => {
    if (!data || typeof data !== 'object') return false;
    
    const dataStr = JSON.stringify(data).toLowerCase();
    const successPatterns = [
      'booking_success',
      'appointment_booked', 
      'meeting_scheduled',
      'event_created',
      'reservation_confirmed',
      'booking_confirmed'
    ];
    
    return successPatterns.some(pattern => dataStr.includes(pattern)) ||
           (data.success === true && findEmailInObject(data)) ||
           (data.status === 'booked') ||
           (data.event?.includes('success'));
  }, [findEmailInObject]);

  const extractGenericData = useCallback((data: any, origin: string) => ({
    provider: getProviderFromOrigin(origin),
    email: findEmailInObject(data),
    name: findNameInObject(data),
    startTime: findDateTimeInObject(data),
    rawData: data,
    confidence: 'medium'
  }), [getProviderFromOrigin, findEmailInObject, findNameInObject, findDateTimeInObject]);

  // Memoized booking success handler
  const handleBookingSuccess = useCallback((bookingData: any) => {
    if (bookingDetected) return; // Prevent duplicate triggers

    console.log('✅ Booking success confirmed:', bookingData);
    
    setBookingDetected(true);
    setDetectedBookingData(bookingData);
    setShowSuccessState(true);

    if (onBookingComplete) {
      onBookingComplete({
        completed: true,
        appointmentDateTime: bookingData.startTime ? new Date(bookingData.startTime) : new Date(),
        bookingData: {
          ...bookingData,
          timeSpent,
          detectedAt: new Date().toISOString(),
          detectionMethod: 'postMessage'
        }
      });
    }

    // Auto-close DISABLED for testing - uncomment when ready
    // setTimeout(() => {
    //   handleClose();
    // }, 5000);
  }, [bookingDetected, onBookingComplete, timeSpent]);

  // Comprehensive calendar message handler
  const handleCalendarMessage = useCallback((data: any, origin: string) => {
    if (bookingDetected) return; // Early return if already detected

    let bookingData: any = null;

    // TidyCal detection patterns
    if (isTidyCalBooking(data)) {
      console.log('TidyCal booking detected:', data);
      bookingData = extractTidyCalData(data);
    }
    
    // Calendly detection patterns  
    else if (isCalendlyBooking(data)) {
      console.log('Calendly booking detected:', data);
      bookingData = extractCalendlyData(data);
    }
    
    // Cal.com detection patterns
    else if (isCalComBooking(data)) {
      console.log('Cal.com booking detected:', data);
      bookingData = extractCalComData(data);
    }
    
    // Generic success patterns
    else if (isGenericSuccessMessage(data)) {
      console.log('Generic booking detected:', data);
      bookingData = extractGenericData(data, origin);
    }
    
    // Fallback: try to find email/date in any object structure
    else if (data && typeof data === 'object') {
      const email = findEmailInObject(data);
      const dateTime = findDateTimeInObject(data);
      
      if (email || dateTime) {
        console.log('📧 Email/date found in unknown pattern:', { email, dateTime });
        bookingData = {
          provider: getProviderFromOrigin(origin),
          email,
          name: findNameInObject(data),
          startTime: dateTime,
          rawData: data,
          confidence: 'low'
        };
      }
    }

    if (bookingData) {
      console.log('✅ Processing booking data:', bookingData);
      handleBookingSuccess(bookingData);
    } else {
      console.log('ℹ️ Message does not match booking patterns:', data);
    }
  }, [
    bookingDetected, 
    handleBookingSuccess, 
    isTidyCalBooking, 
    extractTidyCalData,
    isCalendlyBooking, 
    extractCalendlyData,
    isCalComBooking, 
    extractCalComData,
    isGenericSuccessMessage, 
    extractGenericData,
    findEmailInObject, 
    findNameInObject, 
    findDateTimeInObject, 
    getProviderFromOrigin
  ]);

  // Optimized PostMessage listener
  useEffect(() => {
    if (!isOpen) return;

    const handlePostMessage = (event: MessageEvent) => {
      // Quick trust check - only check for known domains
      const trustedDomains = ['tidycal.com', 'calendly.com', 'cal.com', 'localhost'];
      const isTrusted = trustedDomains.some(domain => event.origin.includes(domain));
      
      if (!isTrusted) return;

      console.log('📨 PostMessage received from', event.origin, ':', event.data);
      handleCalendarMessage(event.data, event.origin);
    };

    window.addEventListener('message', handlePostMessage, true);
    return () => window.removeEventListener('message', handlePostMessage, true);
  }, [isOpen, handleCalendarMessage]);

  // Track time spent
  useEffect(() => {
    if (!isOpen) return;

    const timer = setInterval(() => {
      setTimeSpent(prev => prev + 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [isOpen]);

  const handleClose = useCallback(() => {
    setTimeSpent(0);
    setBookingDetected(false);
    setShowSuccessState(false);
    setDetectedBookingData(null);
    onClose();
  }, [onClose]);

  const isWhiteOrShade = useCallback((color: string) => {
    if (color?.toLowerCase() === "white" || color === "rgba(255,255,255,1)") return true;
    if (/^#fff[a-f0-9]{0,3}$/i.test(color)) return true;

    const rgba = color?.match(/rgba?\((\d+),\s*(\d+),\s*(\d+),?\s*([\d.]+)?\)/);
    if (rgba) {
      const [, r, g, b] = rgba.map(Number);
      return r >= 240 && g >= 240 && b >= 240;
    }
    return false;
  }, []);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center"
      style={{ 
        zIndex: 9999,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        backdropFilter: 'blur(2px)'
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !showSuccessState) {
          handleClose();
        }
      }}
    >
      <div 
        className="bg-white rounded-lg flex flex-col shadow-xl"
        style={{
          width: 'min(95vw, 900px)',
          height: 'min(85vh, 650px)',
          maxWidth: '900px',
          maxHeight: '650px'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div 
          className={`flex justify-between items-center p-4 rounded-t-lg shrink-0 transition-colors duration-500 ${
            showSuccessState ? 'bg-green-600' : ''
          }`}
          style={{ 
            backgroundColor: showSuccessState ? '#16a34a' : (themes?.color1 || '#1E5188') 
          }}
        >
          <h3 
            className="text-lg md:text-xl font-bold truncate pr-4 transition-all duration-500"
            style={{ 
              color: isWhiteOrShade(themes?.color1) ? "#000000" : "#ffffff" 
            }}
          >
            {showSuccessState ? (
              <span className="flex items-center gap-2">
                Booking Confirmed!
              </span>
            ) : (
              `Book Your ${serviceName} Consultation`
            )}
          </h3>
          <button 
            onClick={handleClose}
            className="text-2xl font-bold transition-transform duration-200 hover:scale-125 shrink-0"
            style={{ 
              color: isWhiteOrShade(themes?.color1) ? "#000000" : "#ffffff" 
            }}
            title="Close calendar"
          >
            &times;
          </button>
        </div>

        {/* Calendar Content */}
        <div className="flex-1 relative min-h-0" style={{backgroundColor: "white"}}>
          {showSuccessState && (
            <div className="absolute top-0 left-0 right-0 bg-white border-b border-green-200 p-4 z-10 mb-4">
              <div className="text-center">
                <p className="text-green-800 font-semibold">
                  ✅ Your {serviceName} consultation has been scheduled successfully!
                </p>
                {detectedBookingData?.name && (
                  <p className="text-sm text-green-700 mt-1">
                    Thank you, {detectedBookingData.name}
                  </p>
                )}
                {detectedBookingData?.startTime && (
                  <p className="text-xs text-green-600 mt-1">
                    {new Date(detectedBookingData.startTime).toLocaleString()}
                  </p>
                )}
              </div>
            </div>
          )}
          
          <div className={`w-full h-full ${showSuccessState ? 'pt-24' : ''}`}>
            <iframe
              ref={iframeRef}
              src={calendarLink}
              className="w-full h-full border-0"
              title={`${serviceName} Calendar Booking`}
              allow="camera; microphone; geolocation"
              loading="lazy"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="p-3 border-t bg-gray-50 rounded-b-lg shrink-0">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-2">
            <div className="flex-1 text-center sm:text-left">
              <div className="flex justify-center sm:justify-start items-center gap-4 text-xs text-gray-500">
                <span>⏱️ {Math.floor(timeSpent / 60)}m {timeSpent % 60}s</span>
                {bookingDetected ? (
                  <span className="text-green-600">✅ Booking detected!</span>
                ) : (
                  <span className="text-blue-600">🔍 Waiting for booking...</span>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <button 
                onClick={handleClose}
                className="px-4 py-2 rounded-md transition duration-300 hover:shadow-md text-sm"
                style={{
                  background: showSuccessState ? '#16a34a' : (themes?.color2 || '#A445DE'),
                  color: isWhiteOrShade(themes?.color2) ? "#000000" : "#ffffff",
                }}
              >
                {showSuccessState ? 'Complete' : 'Close'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendarModal;